import axios from "axios";
import { useEffect, useState, useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { formatDate } from "../../../utils/dateFormatter";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import "./LeadDashboard.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function Lead() {
  const [Client, setClient] = useState([]);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("All");

  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const getStatusBucket = (statusValue) => {
    const status = String(statusValue || "").trim().toLowerCase();

    if (status === "cold") return "cold";
    if (["warm", "hot"].includes(status)) return "warm";
    if (
      [
        "pending",
        "followup",
        "follow up",
        "in progress",
        "schedule appointment",
        "proposal sent",
        "hold",
      ].includes(status)
    ) {
      return "inProgress";
    }
    if (status === "win") return "win";
    if (["close", "closed", "other"].includes(status)) return "closed";
    return "other";
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getLeadData`)
      .then((res) => {
        if (res.data) {
          const rows = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.leads)
              ? res.data.leads
              : Array.isArray(res.data?.data)
                ? res.data.data
                : [];
          setClient(rows);
          setFiltered(rows);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  if (!canViewPage(user, "leads")) {
    return (
      <div className="container py-5 text-center">
        <h2 className="text-danger fw-bold">Access Denied</h2>
        <p>You do not have permission to view Leads.</p>
      </div>
    );
  }

  const stats = useMemo(() => {
    const total = Client.length;
    const cold = Client.filter((l) => getStatusBucket(l.status) === "cold").length;
    const warm = Client.filter((l) => getStatusBucket(l.status) === "warm").length;
    const inProgress = Client.filter((l) => getStatusBucket(l.status) === "inProgress").length;
    const win = Client.filter((l) => getStatusBucket(l.status) === "win").length;
    const closed = Client.filter((l) => getStatusBucket(l.status) === "closed").length;
    return { total, cold, warm, inProgress, win, closed };
  }, [Client]);

  const handleCardClick = (statusKey) => {
    if (selectedStatus === statusKey) {
      setFiltered(Client);
      setSelectedStatus("All");
      return;
    }

    setSelectedStatus(statusKey);
    let filteredData = [];

    switch (statusKey) {
      case "Cold":
        filteredData = Client.filter((l) => getStatusBucket(l.status) === "cold");
        break;
      case "Warm":
        filteredData = Client.filter((l) => getStatusBucket(l.status) === "warm");
        break;
      case "InProgress":
        filteredData = Client.filter((l) => getStatusBucket(l.status) === "inProgress");
        break;
      case "Win":
        filteredData = Client.filter((l) => getStatusBucket(l.status) === "win");
        break;
      case "Closed":
        filteredData = Client.filter((l) => getStatusBucket(l.status) === "closed");
        break;
      default:
        filteredData = Client;
    }

    setFiltered(filteredData);
  };

  const moveToClient = async (leadId) => {
    if (!canDo(user, "leads", "edit"))
      return alert("You do not have permission to move leads.");

    try {
      const res = await axios.put(`${API_URL}/api/moveleadtoClient/${leadId}`);
      if (res.status === 200) {
        alert("Lead moved to client successfully!");
        navigate("/admin/client");
      }
    } catch (error) {
      alert("Failed to move lead to client");
    }
  };

  const handleDelete = async (leadId) => {
    if (!canDo(user, "leads", "delete"))
      return alert("You do not have permission to delete leads.");

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this lead?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_URL}/api/DeleteLead/${leadId}`);
      const updatedClients = Client.filter((c) => c.leadId !== leadId);
      setClient(updatedClients);
      setFiltered(updatedClients);
      alert("Lead deleted successfully!");
    } catch (error) {
      alert("Failed to delete lead");
    }
  };

  const statCards = [
    { key: "All", label: "Total Leads", value: stats.total, tone: "all" },
    { key: "Cold", label: "Cold", value: stats.cold, tone: "cold" },
    { key: "Warm", label: "Warm / Hot", value: stats.warm, tone: "warm" },
    { key: "InProgress", label: "In Progress", value: stats.inProgress, tone: "progress" },
    { key: "Win", label: "Won", value: stats.win, tone: "won" },
    { key: "Closed", label: "Closed / Other", value: stats.closed, tone: "closed" },
  ];

  const searchedLeads = filtered.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.leadId?.toLowerCase().includes(q) ||
      c.leadName?.toLowerCase().includes(q) ||
      c.emailId?.toLowerCase().includes(q) ||
      c.phoneNo?.toLowerCase().includes(q) ||
      c.department?.deptName?.toLowerCase().includes(q) ||
      c.service?.serviceName?.toLowerCase().includes(q) ||
      c.status?.toLowerCase().includes(q)
    );
  });

  const exportExcel = () => {
    const rows = searchedLeads.map((c) => ({
      ID: c.leadId || "",
      Name: c.leadName || "",
      Email: c.emailId || "",
      Phone: c.phoneNo || "",
      Department: c.department?.deptName || "",
      Service: c.service?.serviceName || "",
      Price: c.project_price ?? 0,
      Date: formatDate(c.date),
      Status: c.status || "",
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Leads");
    XLSX.writeFile(workbook, "Leads_List.xlsx");
  };

  return (
    <div className="lead-page">
      <div className="lead-shell">
        <div className="lead-hero">
          <div className="lead-hero-copy">
            <p className="lead-eyebrow">Sales CRM</p>
            <h3>Lead List</h3>
            <span>Manage lead pipeline, monitor status, and convert faster</span>
          </div>

          <div className="lead-hero-actions">
            <button type="button" className="lead-export-btn" onClick={exportExcel}>
              Export Excel
            </button>
            {canDo(user, "leads", "add") && (
              <Link to="/admin/addClientLead" className="lead-add-btn">
                + Add Lead
              </Link>
            )}
          </div>
        </div>

        <div className="lead-search-card">
          <label htmlFor="leadSearch" className="lead-search-label">
            Search Leads
          </label>
          <input
            id="leadSearch"
            type="text"
            className="lead-search-input"
            placeholder="Search by lead id, name, email, phone, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="lead-stat-grid">
          {statCards.map((card) => (
            <div className="lead-stat-wrap" key={card.key}>
              <div
                className={`lead-stat-card tone-${card.tone} ${selectedStatus === card.key ? "active" : ""}`}
                onClick={() => handleCardClick(card.key)}
              >
                <h6>{card.label}</h6>
                <h4>{card.value}</h4>
              </div>
            </div>
          ))}
        </div>

        <div className="lead-table-card">
          <div className="table-responsive">
            <table className="table text-center align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Department</th>
                  <th>Service</th>
                  <th>Price</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {searchedLeads.length > 0 ? (
                  searchedLeads.map((clients, index) => {
                    const statusClass = String(clients.status || "")
                      .toLowerCase()
                      .replace(/\s+/g, "-");

                    return (
                      <tr key={index}>
                        <td>
                          {canDo(user, "leads", "view") ? (
                            <button
                              type="button"
                              className="lead-id-link"
                              onClick={() => navigate(`/admin/leadPage/${clients.leadId}`)}
                              title={`Open ${clients.leadId}`}
                            >
                              {clients.leadId}
                            </button>
                          ) : (
                            clients.leadId
                          )}
                        </td>
                        <td>{clients.leadName}</td>
                        <td>{clients.emailId}</td>
                        <td>{clients.phoneNo}</td>
                        <td>{clients.department?.deptName}</td>
                        <td>{clients.service?.serviceName}</td>
                        <td>{clients.project_price}</td>
                        <td>{formatDate(clients.date)}</td>
                        <td>
                          <span className={`lead-status-chip status-${statusClass}`}>{clients.status}</span>
                        </td>

                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            {canDo(user, "leads", "view") && (
                              <button
                                className="btn btn-sm lead-btn view"
                                onClick={() => navigate(`/admin/leadPage/${clients.leadId}`)}
                              >
                                View
                              </button>
                            )}

                            {canDo(user, "leads", "edit") && (
                              <button
                                className="btn btn-sm lead-btn edit"
                                onClick={() =>
                                  navigate(`/admin/updateLeadClient/${clients.leadId}`, {
                                    state: { returnTo: "/admin/leads" },
                                  })
                                }
                              >
                                Update
                              </button>
                            )}

                            {canDo(user, "leads", "edit") && (
                              <button
                                className="btn btn-sm lead-btn move"
                                onClick={() => moveToClient(clients.leadId)}
                              >
                                Move to Client
                              </button>
                            )}

                            {canDo(user, "leads", "delete") && (
                              <button
                                className="btn btn-sm lead-btn delete"
                                onClick={() => handleDelete(clients.leadId)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-3">
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lead;
